/**
 * AbbrLabel (US-17-GR-30)
 *
 * Wraps an abbreviated metric label (CAC, LTV, NPS, MRR, DLQ, ...) in a
 * native <abbr> so admins get a hover/focus tooltip explaining what the
 * abbreviation means. No tooltip library — the browser's native title
 * tooltip is sufficient and keyboard-accessible (tabIndex 0 + cursor-help).
 *
 * Expansions live in glossary.ts — add new abbreviations there, not here.
 */

import type { ReactNode } from 'react';
import { GLOSSARY } from './glossary';

interface AbbrLabelProps {
  /** Glossary key, e.g. "CAC". Falls back to rendering the term unadorned
   * (no tooltip) if the key isn't in the glossary, so a typo degrades
   * gracefully instead of throwing. */
  term: string;
  /** Override the visible text; defaults to `term`. */
  children?: ReactNode;
  className?: string;
}

export function AbbrLabel({ term, children, className }: AbbrLabelProps) {
  const entry = GLOSSARY[term];
  const label = children ?? term;

  if (!entry) {
    return <span className={className}>{label}</span>;
  }

  const title = entry.note ? `${entry.expansion} (${entry.note})` : entry.expansion;

  return (
    <abbr
      title={title}
      aria-label={title}
      tabIndex={0}
      className={[
        'no-underline decoration-dotted underline-offset-2 border-b border-dotted border-zinc-500 cursor-help',
        className ?? '',
      ].join(' ')}
    >
      {label}
    </abbr>
  );
}

export default AbbrLabel;
